import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faCheck, faDesktop, faDownload, faGlobe, faKey, faUsers, faVideo, faXmark } from "@fortawesome/free-solid-svg-icons";
import TemporaryActivationCode from "./TemporaryActivationCode";
import { WINDOWS_INSTALLER_URL } from "./installerDownloads";
import { getAppearancePreference } from "./plansAppearance";
import { ReactComponent as ServicesIcon } from "./assets/services.svg";
import "./DashboardOnboarding.css";
import { productPreviews } from "./productPreviews";

const steps = [
  { label: "Your device", icon: faDesktop, eyebrow: "Install the SignalTuner desktop app", title: "Start with your signal.", description: "Install and activate the SignalTuner desktop app. Generate an activation code below to connect to your account." },
  { label: "Your team", icon: faUsers, eyebrow: "Help your team boost their signal.", title: "Your team, better connected.", introduction: "Join your team or create one of your own.", description: "Bring your teammates into one view so that you can spot connection issues and understand who needs help" },
  { label: "Your services", icon: faGlobe, eyebrow: "Service incident and outage alerts", title: "Monitor the services you use", description: "Get in-app and email notifications when there is an incident or outage in the tools that you use every day to connect." },
  { label: "Your meetings", icon: faVideo, eyebrow: "Analyze a meeting", title: "Get the whole meeting in focus.", description: "Select your meeting service, invite participants, and identify issues impacting your ability to connect." },
  { label: "Ready to go", icon: faCheck, eyebrow: "You're good to go!", title: "Enjoy better connections with SignalTuner", description: "Run analysis, generate reports, and start fixing those issues that leave you disconnected" },
];

function startInstallerDownload() {
  const platform = window.navigator.platform;
  if (["Win32", "Win64", "Windows", "WinCE"].includes(platform)) window.location.href = WINDOWS_INSTALLER_URL;
  else if (["Macintosh", "MacIntel", "MacPPC", "Mac68K"].includes(platform)) window.location.href = "https://signaltuner.com/update/macos/SignalTuner.dmg";
}

export default function DashboardOnboarding({ userHasSeenOnboarding, setUserHasSeenOnboarding, appearance = getAppearancePreference() }) {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const headingRef = useRef(null);
  const downloadStarted = useRef(false);
  const step = steps[currentStep];
  const lastStep = currentStep === steps.length - 1;
  function close() { dialogRef.current?.close(); setOpen(false); }
  function finish() { setUserHasSeenOnboarding(); close(); }

  useEffect(() => {
    if (userHasSeenOnboarding !== false) return;
    setCurrentStep(0); setOpen(true);
    if (!downloadStarted.current) { downloadStarted.current = true; startInstallerDownload(); }
  }, [userHasSeenOnboarding]);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!dialogRef.current.open) dialogRef.current.showModal();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    headingRef.current?.focus();
  }, [open, currentStep]);
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const body = bodyRef.current;
    const image = body.querySelector("figure > img");
    if (!image) return;
    const preview = image.parentElement;
    const copy = body.querySelector(".onboarding-copy");
    const layout = body.querySelector(".onboarding-layout");
    const chrome = [...dialog.children].filter(element => element !== body);
    const pixels = value => parseFloat(value) || 0;
    const fitImage = () => {
      const dialogStyle = getComputedStyle(dialog);
      const bodyStyle = getComputedStyle(body);
      const previewRect = preview.getBoundingClientRect();
      const copyRect = copy.getBoundingClientRect();
      const stacked = previewRect.left < copyRect.right;
      // Measure the available viewport independently of the image's current size,
      // so previews can grow again when the window gets taller.
      const available = Math.min(window.innerHeight, window.visualViewport?.height ?? window.innerHeight)
        - pixels(dialogStyle.getPropertyValue("--onboard-viewport-gap"))
        - pixels(dialogStyle.borderTopWidth) - pixels(dialogStyle.borderBottomWidth)
        - chrome.reduce((height, element) => height + element.getBoundingClientRect().height, 0)
        - pixels(bodyStyle.paddingTop) - pixels(bodyStyle.paddingBottom)
        - (stacked ? copyRect.height + pixels(getComputedStyle(layout).rowGap) : 0)
        - (previewRect.height - image.getBoundingClientRect().height);
      image.style.maxHeight = `${Math.max(0, Math.floor(available))}px`;
    };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fitImage) : null;
    [...chrome, copy, preview].forEach(element => observer?.observe(element));
    window.addEventListener("resize", fitImage);
    window.visualViewport?.addEventListener("resize", fitImage);
    image.addEventListener("load", fitImage);
    fitImage();
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", fitImage);
      window.visualViewport?.removeEventListener("resize", fitImage);
      image.removeEventListener("load", fitImage);
      image.style.removeProperty("max-height");
    };
  }, [open, currentStep]);

  if (!open) return null;
  return <dialog ref={dialogRef} className={`dashboard-onboarding-modal dashboard-onboarding-modal--${appearance}`}
    aria-labelledby="onboarding-title" aria-describedby="onboarding-intro"
    onCancel={event => { event.preventDefault(); close(); }} onClose={() => setOpen(false)}>
    <header className="onboarding-header">
      <div className="onboarding-brand"><img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="" />
        <div><p className="onboarding-eyebrow">LET'S GET YOU CONNECTED</p><h2 id="onboarding-title">Welcome to SignalTuner</h2></div>
      </div>
      <button type="button" className="onboarding-close" aria-label="Close setup" onClick={close}><FontAwesomeIcon icon={faXmark} aria-hidden="true" /></button>
      <p id="onboarding-intro">A quick tour of your connection toolkit.</p>
    </header>
    <ol className="onboarding-steps" aria-label="Setup steps">{steps.map((item, index) => <li key={item.label}
      aria-current={index === currentStep ? "step" : undefined} data-complete={index < currentStep}>
      <span className="onboarding-step-number" aria-hidden="true">{index < currentStep ? <FontAwesomeIcon icon={faCheck} /> : index + 1}</span>
      <span>{item.label}</span>
    </li>)}</ol>
    <div className="onboarding-body" ref={bodyRef}>
      <div className="onboarding-layout">
        <section className={`onboarding-copy${currentStep === 0 ? " onboarding-copy--desktop" : ""}`} aria-labelledby="onboarding-step-title">
          <p className="onboarding-eyebrow" aria-live="polite">{step.eyebrow || `STEP ${currentStep + 1} OF ${steps.length}`}</p>
          <h3 id="onboarding-step-title" ref={headingRef} tabIndex={-1}>{step.title}</h3>
          {step.introduction && <p className="onboarding-description">{step.introduction}</p>}
          <p className="onboarding-description">{step.description}</p>
          {currentStep === 0 ? <div className="onboarding-desktop-actions">
              <div className="onboarding-download-actions"><a className="onboarding-button onboarding-button--primary" href={WINDOWS_INSTALLER_URL}><FontAwesomeIcon icon={faDownload} aria-hidden="true" />Download for Windows</a>
                <Link to="/downloads" onClick={close}>Other downloads</Link></div>
              <TemporaryActivationCode compact generateIcon={<FontAwesomeIcon icon={faKey} aria-hidden="true" />} />
              <p className="onboarding-description">The SignalTuner desktop app measures and analyzes your connection. Insights are provided to identify and resolve connectivity issues.</p>
          </div> : lastStep ? <button type="button" className="onboarding-button onboarding-button--primary onboarding-finish-cta" onClick={finish}>Let's go!</button>
            : <p className="onboarding-page-hint">{currentStep === 2 ? <ServicesIcon className="onboarding-services-icon" aria-hidden="true" focusable="false" /> : <FontAwesomeIcon icon={step.icon} aria-hidden="true" />}Find this on the {currentStep === 1 ? "Team" : currentStep === 2 ? "Services" : "Meetings"} page.</p>}
        </section>
        {currentStep === 0 ? <figure className="onboarding-desktop-preview">
          <img src={`${process.env.PUBLIC_URL}/${productPreviews[0].file}`} alt={productPreviews[0].alt} />
        </figure> : currentStep === 1 ? <figure className="onboarding-team-preview">
          <img src={`${process.env.PUBLIC_URL}/${productPreviews[1].file}`} alt={productPreviews[1].alt} />
        </figure> : currentStep === 2 ? <figure className="onboarding-services-preview">
          <img src={`${process.env.PUBLIC_URL}/${productPreviews[2].file}`} alt={productPreviews[2].alt} />
        </figure> : currentStep === 3 ? <figure className="onboarding-meetings-preview">
          <img src={`${process.env.PUBLIC_URL}/${productPreviews[3].file}`} alt={productPreviews[3].alt} />
        </figure> : <figure className="onboarding-analysis-preview">
          <img src={`${process.env.PUBLIC_URL}/${productPreviews[4].file}`} alt={productPreviews[4].alt} />
        </figure>}
      </div>
    </div>
    <footer className="onboarding-footer">
      <button type="button" className="onboarding-skip" onClick={close}>Skip for now</button>
      <div>{currentStep > 0 && <button type="button" className="onboarding-button" onClick={() => setCurrentStep(value => value - 1)}><FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />Back</button>}
        <button type="button" className="onboarding-button onboarding-button--primary" onClick={lastStep ? finish : () => setCurrentStep(value => value + 1)}>
          {lastStep ? "Finish setup" : "Next"}<FontAwesomeIcon icon={lastStep ? faCheck : faArrowRight} aria-hidden="true" />
        </button>
      </div>
    </footer>
  </dialog>;
}
